import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import moment from "moment";
import strings from "translate/language";
import { addFollowUp, followupList } from "reduxStore/action/followUpActions";
import { getFollowupStatus } from "reduxStore/action/masterActions";
import {
  getSiteVisitStatusData,
  updateSiteVisitStatus,
} from "reduxStore/action/siteVisitActions";
import { handleListResponse, seo } from "utils/utils";
import {
  filterFollowupHistoryRows,
  historyDescription,
  historyDescriptionLabel,
  resolveWalkinRemark,
} from "utils/followupHistoryDisplay";
import Breadcrumb from "component/common/breadcrumb";
import AppointmentScheduleFields from "../Appointment/AppointmentScheduleFields";
import "./FollowupListing.css";
import "../Appointment/AppointmentListing.css";

/** Call / WhatsApp master id — date + time required when selected (existing behaviour). */
const CALL_WHATSAPP_STATUS_ID = "6360c6d52ca46e9d3636fbf4";

const selectPortalProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 10060 }),
  },
};

const formatHistoryWhen = (item) => {
  if (!item?.next_followup_date) return "—";
  const datePart = moment(item.next_followup_date).format("DD-MM-YYYY");
  const timePart =
    item.followup_time && String(item.followup_time).toLowerCase() !== "null"
      ? String(item.followup_time).trim()
      : "";
  return timePart ? `${datePart} · ${timePart}` : datePart;
};

const UpdateStatusFollow = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formVaild, setFormVaild] = useState({});
  const [viewData, setViewData] = useState([]);
  const [walkinRemark, setWalkinRemark] = useState("");
  const params = useParams();
  const { lead_id = null, appointment_id = null } = params || {};
  const [siteVisitMapping, setSiteVisitMapping] = useState(null);
  const allFollowupDetail = useSelector((state) => state.followUp) || {};
  const followupStatus = useSelector((state) => state.followupstatus);
  const { response: res } = useSelector((state) => state.login);
  // Only leave after a submit from this screen — ignore stale addFollowupresponse in Redux.
  const submitPendingRef = useRef(false);

  const navigatpage = async (navname) => {
    navigate(navname);
  };

  const [form, setForm] = useState({
    followup_status: "",
    followup_date: "",
    next_followup_date: "",
    followup_time: "",
    remark: "",
  });

  const statusOptions = useMemo(() => {
    const rows = followupStatus?.response?.data;
    if (!Array.isArray(rows)) return [];
    return rows.map((item) => ({
      value: String(item._id),
      label: item.title,
    }));
  }, [followupStatus]);

  const selectedStatus = useMemo(
    () =>
      statusOptions.find((o) => o.value === String(form.followup_status)) ||
      null,
    [statusOptions, form.followup_status],
  );

  const needsSchedule = form.followup_status === CALL_WHATSAPP_STATUS_ID;

  /**
   * Sidebar: Call / Next Day Call / WhatsApp / Follow-Up only.
   * First empty Call → CM/SCM walk-in; GRE first row keeps its own remark.
   */
  const followupHistory = useMemo(
    () => filterFollowupHistoryRows(viewData),
    [viewData],
  );

  const latestHistory =
    followupHistory.length > 0 ? followupHistory[0] : null;

  useEffect(() => {
    if (allFollowupDetail.response) {
      const { response, addFollowupresponse = null } = allFollowupDetail;
      if (response?.status === 200) {
        const rows = handleListResponse(response.data);
        setViewData(rows);
        setWalkinRemark(resolveWalkinRemark(response, rows));
      } else {
        setViewData([]);
        setWalkinRemark("");
      }
      if (
        submitPendingRef.current &&
        addFollowupresponse &&
        addFollowupresponse?.status === 200
      ) {
        submitPendingRef.current = false;
        if (siteVisitMapping?.jv_site_visit_id) {
          const payload = {
            jvSiteVisitId: siteVisitMapping.jv_site_visit_id,
            closingStatus: "Follow Up",
            role: res?.data?.role_title || "auto",
          };
          dispatch(updateSiteVisitStatus(payload))
            .then(() => {})
            .catch(() => {})
            .finally(() => {
              navigatpage("/followUp");
            });
        } else {
          navigatpage("/followUp");
        }
      }
    }
  }, [allFollowupDetail]);

  useEffect(() => {
    dispatch(followupList({ lead_id: lead_id }));
  }, []);

  useEffect(() => {
    if (appointment_id) {
      dispatch(getSiteVisitStatusData({ appointment_id }))
        .then((mappingRes) => {
          if (mappingRes?.data?.length > 0) {
            setSiteVisitMapping(mappingRes.data[0]);
          }
        })
        .catch(() => {});
    }
  }, [appointment_id, dispatch]);

  const statusUpdate = () => {
    if (!handleValid()) {
      const post = {
        followup_status: form.followup_status,
        next_followup_date: form.next_followup_date,
        followup_time: form.followup_time,
        remark: form.remark,
        lead_id: lead_id,
      };
      if (appointment_id) {
        post.appointment_id = appointment_id;
        post.deposition_source = "appointment_details";
      }
      submitPendingRef.current = true;
      dispatch(addFollowUp(post));
    }
  };

  const handleInputField = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleScheduleDate = (date) => {
    setForm({
      ...form,
      next_followup_date: date ? moment(date).format("YYYY-MM-DD") : "",
    });
  };

  const handleScheduleTime = (followup_time) => {
    setForm({
      ...form,
      followup_time: followup_time || "",
    });
  };

  useEffect(() => {
    dispatch(getFollowupStatus());
  }, []);

  const handleValid = () => {
    let err = {};
    let formError = false;

    const currentDateTime = moment();
    const selectedDateTime = moment(
      form.next_followup_date + " " + form.followup_time,
      "YYYY-MM-DD hh:mm A",
    );

    if (selectedDateTime.isBefore(currentDateTime, "minute")) {
      if (
        form.next_followup_date === moment().format("YYYY-MM-DD") &&
        form.followup_time
      ) {
        formError = true;
        err.followup_time = strings.invalidTimeError;
      }
    }

    if (!form.next_followup_date && needsSchedule) {
      formError = true;
      err.next_followup_date = strings.followup_dateErr;
    }
    if (!form.remark) {
      formError = true;
      err.remark = strings.remarkErr;
    }

    if (!form.followup_time && needsSchedule) {
      formError = true;
      err.followup_time = strings.followup_timeErr;
    }
    if (form.followup_status === "") {
      formError = true;
      err.followup_status = strings.followupStatusErr;
    }

    setFormVaild(err);
    return formError;
  };

  useEffect(() => {
    seo({
      title: "Update Followup Status",
      metaDescription: "Update followup status",
      canonicalLink: window.location.href,
    });
  }, []);

  return (
    <div className="page-body justo-followup-page">
      <Breadcrumb
        title={strings.newFollowup}
        url={"/followup"}
        parentListing={strings.followupList}
        titleactive={strings.NewFollowup}
        isBackBtn={true}
      />
      <div className="container-fluid justo-followup">
        <div className="justo-followup__card justo-followup-form">
          <div className="justo-followup__top justo-followup-form__top">
            <div>
              <h2 className="justo-followup__title">{strings.updateFollowup}</h2>
              <p className="justo-followup__subtitle">
                Form on the left · history on the right. Update sits with the
                follow-up fields.
              </p>
            </div>
            <div className="justo-followup-form__top-actions">
              <button
                type="button"
                className="justo-followup__btn justo-followup__btn--ghost"
                onClick={() => navigatpage("/followUp")}
              >
                {strings.cancel || "Cancel"}
              </button>
            </div>
          </div>

          {latestHistory ? (
            <div className="justo-followup-form__meta">
              <div className="justo-followup-form__fact">
                <span className="justo-followup-form__fact-label">
                  {strings.followupBy}
                </span>
                <span className="justo-followup-form__fact-value">
                  {latestHistory.creator_name || "—"}
                </span>
              </div>
              <div className="justo-followup-form__fact">
                <span className="justo-followup-form__fact-label">
                  {strings.status}
                </span>
                <span className="justo-followup-form__fact-value">
                  {latestHistory.followup_status || "—"}
                </span>
              </div>
              <div className="justo-followup-form__fact">
                <span className="justo-followup-form__fact-label">
                  {strings.date} & {strings.time}
                </span>
                <span className="justo-followup-form__fact-value">
                  {formatHistoryWhen(latestHistory)}
                </span>
              </div>
            </div>
          ) : null}

          <div className="justo-followup-form__body">
            <section className="justo-followup-form__panel">
              <h3 className="justo-followup-form__section-title">
                {strings.NewFollowup || strings.newFollowup}
              </h3>
              <div className="justo-followup-form__grid">
                <div className="justo-followup__field justo-followup-form__field--full">
                  <label className="justo-followup__label">
                    {strings.status}
                    <span className="required-field" />
                  </label>
                  <Select
                    classNamePrefix="justo-followup-select"
                    options={statusOptions}
                    value={selectedStatus}
                    onChange={(opt) =>
                      setForm({
                        ...form,
                        followup_status: opt?.value || "",
                      })
                    }
                    placeholder={strings.selectstatus}
                    isSearchable
                    isClearable
                    {...selectPortalProps}
                  />
                  {formVaild.followup_status ? (
                    <span className="justo-followup__error">
                      {formVaild.followup_status}
                    </span>
                  ) : null}
                </div>

                {needsSchedule ? (
                  <div className="justo-followup__field justo-followup-form__field--full">
                    <AppointmentScheduleFields
                      dateId="next_followup_date"
                      hourId="followup_hour"
                      selectedDate={
                        form.next_followup_date
                          ? moment(
                              form.next_followup_date,
                              "YYYY-MM-DD",
                            ).toDate()
                          : null
                      }
                      onDateChange={handleScheduleDate}
                      appointmentTime={form.followup_time}
                      onTimeChange={handleScheduleTime}
                      dateError={formVaild.next_followup_date}
                      timeError={formVaild.followup_time}
                      minDate={moment().startOf("day").toDate()}
                      dateLabel={strings.date}
                      timeLabel={strings.time}
                      datePlaceholder={strings.selectAppointmentDate}
                      showTimeHint={false}
                    />
                  </div>
                ) : null}

                <div className="justo-followup__field justo-followup-form__field--full">
                  <label className="justo-followup__label" htmlFor="followup_remark">
                    {strings.description}
                    <span className="required-field" />
                  </label>
                  <textarea
                    id="followup_remark"
                    className="justo-followup__input justo-followup-form__textarea"
                    name="remark"
                    value={form.remark}
                    onChange={handleInputField}
                    rows={4}
                    placeholder="Add a short note for this follow-up"
                  />
                  {formVaild.remark ? (
                    <span className="justo-followup__error">
                      {formVaild.remark}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="justo-followup-form__actions">
                <button
                  type="button"
                  className="justo-followup__btn justo-followup__btn--solid"
                  onClick={statusUpdate}
                >
                  <i className="fa fa-check" aria-hidden="true" />
                  {strings.update}
                </button>
              </div>
            </section>

            <section className="justo-followup-form__panel justo-followup-form__panel--history">
              <h3 className="justo-followup-form__section-title">
                Follow-up history
              </h3>
              <div className="justo-followup-form__history-scroll">
                {followupHistory.length > 0 ? (
                  followupHistory.map((item, index) => (
                    <article
                      key={item._id || index}
                      className="justo-followup-form__history-card"
                    >
                      <div className="justo-followup-form__history-head">
                        <span className="justo-followup__status">
                          <i className="fa fa-phone" aria-hidden="true" />
                          <span className="justo-followup__status-label">
                            {item.followup_status || "—"}
                          </span>
                        </span>
                        <span className="justo-followup-form__history-when">
                          {formatHistoryWhen(item)}
                        </span>
                      </div>
                      <dl className="justo-followup-form__history-facts">
                        <div>
                          <dt>{strings.followupBy}</dt>
                          <dd>{item.creator_name || "—"}</dd>
                        </div>
                        <div>
                          <dt>
                            {historyDescriptionLabel(
                              item,
                              walkinRemark,
                              followupHistory,
                            )}
                          </dt>
                          <dd>
                            {historyDescription(
                              item,
                              walkinRemark,
                              followupHistory,
                            )}
                          </dd>
                        </div>
                      </dl>
                    </article>
                  ))
                ) : (
                  <p className="justo-followup-detail__empty">
                    {strings.notavailable}
                  </p>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateStatusFollow;
